import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, limit, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import employeeService from '../../firebase/employeeService';
import './ManualAttendanceForm.css';

/**
 * ManualAttendanceForm Component
 * Provides a UI for admins to manually add attendance records
 */
const ManualAttendanceForm = ({ onClose, onSuccess }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [attendanceDate, setAttendanceDate] = useState('');
  const [clockInTime, setClockInTime] = useState('');
  const [clockOutTime, setClockOutTime] = useState('');
  const [location, setLocation] = useState('manual-entry');
  const [reason, setReason] = useState('Admin added past attendance record');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Warehouse coordinates (Citilink Warehouse Complex)
  const warehouseCoords = {
    latitude: 1.2762970485308482,
    longitude: 103.79506703099759
  };

  // Default coordinates for manual entries
  const defaultCoords = {
    latitude: 1.3521,
    longitude: 103.8198
  };

  // Load employees on component mount
  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const result = await employeeService.getEmployees();
      if (result.success) {
        setEmployees(result.employees.filter(emp => emp.isActive !== false));
      } else {
        setError('Failed to load employees');
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setError('Failed to load employees');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!selectedEmployee) {
        throw new Error('Please select an employee');
      }
      if (!attendanceDate) {
        throw new Error('Please select a date');
      }
      if (!clockInTime && !clockOutTime) {
        throw new Error('Please provide at least clock-in or clock-out time');
      }

      // Find employee data
      const employee = employees.find(emp => emp.uid === selectedEmployee);
      if (!employee) {
        throw new Error('Selected employee not found');
      }

      const isWarehouse = location === 'warehouse';
      const coords = isWarehouse ? warehouseCoords : defaultCoords;

      let successCount = 0;
      const results = [];

      // Create clock-in record if provided
      if (clockInTime) {
        const clockInDateTime = new Date(`${attendanceDate}T${clockInTime}`);
        const clockInRecord = {
          uid: employee.uid,
          employeeId: employee.employeeId,
          type: 'clock-in',
          timestamp: clockInDateTime,
          location: {
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: 10
          },
          isWarehouse: isWarehouse,
          photoUrl: 'manual-entry-no-photo',
          photoMetadata: {
            captureTime: clockInDateTime,
            deviceInfo: { source: 'manual-entry' },
            isLiveCapture: false
          },
          status: 'approved',
          validationResults: {
            photoRecency: true,
            gpsAccuracy: true,
            warehouseProximity: isWarehouse,
            duplicateCheck: true,
            photoAuthenticity: false,
            overallScore: 85
          },
          isManualEntry: true,
          manualEntryReason: reason,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const clockInRef = await addDoc(collection(db, 'attendance'), clockInRecord);
        results.push({ type: 'clock-in', id: clockInRef.id, time: clockInTime });
        successCount++;
      }

      // Create clock-out record if provided
      if (clockOutTime) {
        const clockOutDateTime = new Date(`${attendanceDate}T${clockOutTime}`);
        const clockOutRecord = {
          uid: employee.uid,
          employeeId: employee.employeeId,
          type: 'clock-out',
          timestamp: clockOutDateTime,
          location: {
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: 10
          },
          isWarehouse: isWarehouse,
          photoUrl: 'manual-entry-no-photo',
          photoMetadata: {
            captureTime: clockOutDateTime,
            deviceInfo: { source: 'manual-entry' },
            isLiveCapture: false
          },
          status: 'approved',
          validationResults: {
            photoRecency: true,
            gpsAccuracy: true,
            warehouseProximity: isWarehouse,
            duplicateCheck: true,
            photoAuthenticity: false,
            overallScore: 85
          },
          isManualEntry: true,
          manualEntryReason: reason,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const clockOutRef = await addDoc(collection(db, 'attendance'), clockOutRecord);
        results.push({ type: 'clock-out', id: clockOutRef.id, time: clockOutTime });
        successCount++;
      }

      // Update employee's last attendance timestamp
      await updateDoc(doc(db, 'employees', employee.uid), {
        lastAttendanceAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setSuccess(`Successfully created ${successCount} attendance record(s) for ${employee.name}`);
      
      // Reset form
      setSelectedEmployee('');
      setAttendanceDate('');
      setClockInTime('');
      setClockOutTime('');
      setReason('Admin added past attendance record');

      // Notify parent component
      if (onSuccess) {
        onSuccess({
          employee: employee,
          records: results,
          date: attendanceDate
        });
      }

    } catch (error) {
      console.error('Error adding manual attendance:', error);
      setError(error.message || 'Failed to add attendance record');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = () => {
    // This could open a modal for bulk CSV upload or multiple entries
    alert('Bulk add feature coming soon! For now, use the console scripts for bulk operations.');
  };

  return (
    <div className="manual-attendance-form">
      <div className="form-header">
        <h3>📝 Add Manual Attendance Record</h3>
        <button className="close-btn" onClick={onClose} type="button">×</button>
      </div>

      <form onSubmit={handleSubmit} className="attendance-form">
        {/* Employee Selection */}
        <div className="form-group">
          <label htmlFor="employee">Employee *</label>
          <select
            id="employee"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            required
            disabled={loading}
          >
            <option value="">Select an employee...</option>
            {employees.map(employee => (
              <option key={employee.uid} value={employee.uid}>
                {employee.name} ({employee.employeeId})
              </option>
            ))}
          </select>
        </div>

        {/* Date Selection */}
        <div className="form-group">
          <label htmlFor="date">Date *</label>
          <input
            type="date"
            id="date"
            value={attendanceDate}
            onChange={(e) => setAttendanceDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            required
            disabled={loading}
          />
        </div>

        {/* Time Inputs */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clockIn">Clock In Time</label>
            <input
              type="time"
              id="clockIn"
              value={clockInTime}
              onChange={(e) => setClockInTime(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="clockOut">Clock Out Time</label>
            <input
              type="time"
              id="clockOut"
              value={clockOutTime}
              onChange={(e) => setClockOutTime(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        {/* Location Selection */}
        <div className="form-group">
          <label htmlFor="location">Location</label>
          <select
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={loading}
          >
            <option value="manual-entry">Manual Entry (Default)</option>
            <option value="warehouse">Warehouse</option>
          </select>
        </div>

        {/* Reason */}
        <div className="form-group">
          <label htmlFor="reason">Reason for Manual Entry</label>
          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this attendance record is being added manually..."
            rows="3"
            disabled={loading}
          />
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="message error-message">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="message success-message">
            ✅ {success}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={handleBulkAdd}
            disabled={loading}
          >
            📊 Bulk Add
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? '⏳ Adding...' : '✅ Add Record'}
          </button>
        </div>
      </form>

      {/* Help Text */}
      <div className="form-help">
        <h4>💡 Tips:</h4>
        <ul>
          <li>You can add just clock-in, just clock-out, or both</li>
          <li>All manual entries are automatically approved</li>
          <li>Records are clearly marked as manual entries</li>
          <li>For bulk operations, use the console scripts provided</li>
        </ul>
      </div>
    </div>
  );
};

export default ManualAttendanceForm;
